import { useEffect, useState } from 'react';
import { useTheme } from 'next-themes';
import { CodeBlockShell } from '@reflex/ui/components/content/MarkdownContent';

const SUPPORTED_LANGS = [
  'bash',
  'css',
  'diff',
  'go',
  'html',
  'java',
  'json',
  'jsx',
  'markdown',
  'python',
  'rust',
  'shell',
  'sql',
  'tsx',
  'typescript',
  'yaml',
] as const;

type SupportedLang = (typeof SUPPORTED_LANGS)[number];
const SUPPORTED_SET = new Set<string>(SUPPORTED_LANGS);

const LANG_ALIASES: Record<string, SupportedLang> = {
  ts: 'typescript',
  js: 'jsx',
  javascript: 'jsx',
  sh: 'bash',
  zsh: 'bash',
  yml: 'yaml',
  md: 'markdown',
  py: 'python',
};

const LANG_LABELS: Record<string, string> = {
  bash: 'bash',
  css: 'css',
  diff: 'diff',
  go: 'go',
  html: 'html',
  java: 'java',
  json: 'json',
  jsx: 'javascript',
  markdown: 'markdown',
  python: 'python',
  rust: 'rust',
  shell: 'shell',
  sql: 'sql',
  tsx: 'tsx',
  typescript: 'typescript',
  yaml: 'yaml',
};

let highlighterPromise: Promise<import('shiki').Highlighter> | null = null;

async function getHighlighter(): Promise<import('shiki').Highlighter> {
  if (!highlighterPromise) {
    highlighterPromise = import('shiki').then((shiki) =>
      shiki.createHighlighter({
        themes: ['github-light', 'github-dark'],
        langs: [...SUPPORTED_LANGS],
      }),
    );
  }
  return highlighterPromise;
}

export interface ShikiCodeBlockProps {
  code: string;
  lang?: string;
  /**
   * Optional filename rendered in the block's header — surfaced when fences
   * use the `lang:filename` syntax (e.g. ` ```typescript:src/foo.ts `).
   */
  filename?: string;
  className?: string;
}

function normalizeLang(lang?: string): SupportedLang | null {
  if (!lang) return null;
  const lower = lang.toLowerCase();
  if (SUPPORTED_SET.has(lower)) return lower as SupportedLang;
  if (lower in LANG_ALIASES) return LANG_ALIASES[lower];
  return null;
}

/**
 * Renders a syntax-highlighted code block via Shiki. Lazy-imports the
 * highlighter on first use and re-themes when light/dark mode toggles.
 * Falls back to a plain `<pre>` for unknown languages.
 *
 * Wraps everything in `CodeBlockShell` so the chrome (border, header,
 * copy button, padding) stays visually identical to plain prose code
 * blocks rendered elsewhere in the app.
 */
export function ShikiCodeBlock({ code, lang, filename, className }: ShikiCodeBlockProps) {
  const { resolvedTheme } = useTheme();
  const [html, setHtml] = useState<string | null>(null);
  const normalized = normalizeLang(lang);
  const label = normalized ? (LANG_LABELS[normalized] ?? normalized) : (lang ?? 'text');

  useEffect(() => {
    if (normalized === null) {
      setHtml(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      const highlighter = await getHighlighter();
      const theme = resolvedTheme === 'dark' ? 'github-dark' : 'github-light';
      const out = highlighter.codeToHtml(code, { lang: normalized, theme });
      if (!cancelled) setHtml(out);
    })();
    return () => {
      cancelled = true;
    };
  }, [code, normalized, resolvedTheme]);

  if (normalized === null || html === null) {
    return (
      <CodeBlockShell
        code={code}
        lang={label}
        filename={filename}
        className={className}
        testId="docs-code-block"
        copyTestId="docs-code-copy"
      >
        <pre
          data-testid="docs-code-content"
          className="overflow-x-auto px-4 py-3 font-mono text-[13px] leading-[1.6] text-foreground/90"
        >
          <code>{code}</code>
        </pre>
      </CodeBlockShell>
    );
  }

  return (
    <CodeBlockShell
      code={code}
      lang={label}
      filename={filename}
      className={`docs-shiki${className ? ` ${className}` : ''}`}
      testId="docs-code-block"
      copyTestId="docs-code-copy"
    >
      {/* `dangerouslySetInnerHTML` is safe here because `highlighter.codeToHtml`
          fully HTML-escapes the input `code` before wrapping tokens in
          tagged `<span>`s. The injected markup is Shiki-controlled (tokens
          + theme styles); user input never reaches the DOM unescaped. */}
      <div
        data-testid="docs-code-content"
        className="overflow-x-auto font-mono text-[13px] leading-[1.6] [&>pre]:!m-0 [&>pre]:!bg-transparent [&>pre]:!px-4 [&>pre]:!py-3"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </CodeBlockShell>
  );
}
